import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spin, Empty } from 'antd';
import {
  ShoppingCartOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  TagsOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { offerService, productService, storageUrl } from '../services/api';
import './Home.css';
import './Offers.css';

const PLACEHOLDER = 'https://placehold.co/400x400/e2e8f0/64748b?text=Product';

const rs = (n) => 'Rs ' + Number(n || 0).toLocaleString('en-IN');
const marketPrice = (p) => {
  if (p.discount_percentage) return Math.round(p.price / (1 - p.discount_percentage / 100) / 100) * 100;
  if (p.discount) return Math.round(p.price / (1 - p.discount / 100) / 100) * 100;
  return null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const discountLabel = (offer) => {
  if (offer.discount_percentage) return `${offer.discount_percentage}% OFF`;
  if (offer.discount_amount) return `Rs ${offer.discount_amount} OFF`;
  if (offer.discount_type === 'bogo') return 'BOGO';
  if (offer.discount_type === 'free_shipping') return 'Free Shipping';
  return 'Deal';
};

const Offers = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const requireAuth = (path) => {
    const token = localStorage.getItem('authToken');
    if (token) navigate(path);
    else navigate('/login', { state: { from: path } });
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [productsRes, offersRes] = await Promise.all([
          productService.getProducts({ limit: 8, sort: 'created_at', order: 'desc' })
            .then((r) => r.data?.data || r.data || [])
            .catch(() => []),
          offerService.getOffers()
            .then((r) => r.data?.data || r.data || [])
            .catch(() => []),
        ]);

        const productList = Array.isArray(productsRes) ? productsRes : [];
        setDeals(
          productList.map((p) => ({
            id: p.id,
            brand: p.brand || '',
            name: p.name,
            desc: p.description ? p.description.slice(0, 80) : '',
            price: p.sale_price || p.price,
            originalPrice: p.price,
            discount: p.discount_percentage || (p.sale_price && p.price ? Math.round((1 - p.sale_price / p.price) * 100) : 0),
            img: storageUrl(p.images?.[0]?.url || p.images?.[0]?.image_url) || PLACEHOLDER,
          })),
        );

        const offerList = Array.isArray(offersRes) ? offersRes : [];
        setOffers(offerList);
      } catch (err) {
        console.error('Error loading offers:', err);
        setError(err.message || 'Failed to load offers.');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, []);

  const now = new Date();

  return (
    <div className="offers-page">
      <header className="page-band">
        <div className="page-band-inner">
          <h1>Special offers on electronics you love</h1>
        </div>
      </header>

      {/* Featured deal banners */}
      <section className="home-section">
        <div className="deal-banners">
          <div className="deal-banner deal-banner--amber">
            <span className="deal-kicker">
              <ThunderboltOutlined /> Dashain Dhamaka
            </span>
            <h2>Up to 35% off appliances &amp; gadgets</h2>
            <p>
              From smart TVs to washing machines, festival prices with free installation and easy
              EMI. Stock is limited, grab yours today.
            </p>
            <Button onClick={() => navigate('/products')}>
              Shop the sale <ArrowRight size={16} style={{ marginLeft: 6, verticalAlign: -3 }} />
            </Button>
          </div>
          <div className="deal-banner deal-banner--blue">
            <span className="deal-kicker">
              <TagsOutlined /> Service combo
            </span>
            <div className="deal-off">
              20%
              <small>off any repair</small>
            </div>
            <p>Book AC, fridge or TV repair online and save on your first service.</p>
            <Button onClick={() => requireAuth('/service-catalog')}>Book a service</Button>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="home-section" style={{ paddingTop: 0 }}>
        <div className="section-header">
          <h2>Today&apos;s best deals</h2>
          <p>Hand-picked discounts on the appliances and electronics customers want most.</p>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : deals.length === 0 ? (
          <Empty description="No deals available right now" />
        ) : (
          <Row gutter={[24, 24]}>
            {deals.map((product) => (
              <Col xs={12} sm={12} md={6} key={product.id}>
                <Card
                  hoverable
                  className="product-card"
                  cover={
                    <div className="product-cover">
                      <img
                        alt={product.name}
                        src={product.img}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER; }}
                      />
                      {product.discount > 0 && (
                        <span className="product-badge">{product.discount}% off</span>
                      )}
                      <button
                        type="button"
                        className="product-wish"
                        aria-label="Add to wishlist"
                        onClick={(e) => {
                          e.stopPropagation();
                          requireAuth('/wishlist');
                        }}
                      >
                        <HeartOutlined />
                      </button>
                    </div>
                  }
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {product.brand && <div className="product-brand">{product.brand}</div>}
                  <div className="product-title">{product.name}</div>
                  <div className="product-desc">{product.desc}</div>
                  <div className="product-price-row">
                    <span className="product-price">{rs(product.price)}</span>
                    {product.originalPrice && product.originalPrice > product.price && (
                      <span className="product-original">{rs(product.originalPrice)}</span>
                    )}
                  </div>
                  <Button
                    type="primary"
                    block
                    className="product-cart-btn"
                    icon={<ShoppingCartOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      requireAuth('/cart');
                    }}
                  >
                    Add to Cart
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        <div className="section-cta">
          <Button size="large" onClick={() => navigate('/products')}>
            View all products <ArrowRight size={16} style={{ marginLeft: 4, verticalAlign: -3 }} />
          </Button>
        </div>
      </section>

      {/* Live promotions from the API */}
      {loading ? null : error ? null : offers.length > 0 ? (
        <section className="home-section" style={{ paddingTop: 0 }}>
          <div className="section-header">
            <h2>Running promotions</h2>
            <p>Active coupons and campaigns you can redeem right now.</p>
          </div>
          <Row gutter={[24, 24]}>
            {offers.map((offer) => {
              const isExpired = offer.valid_until && new Date(offer.valid_until) < now;
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={offer.id}>
                  <Card
                    className={`promo-card${isExpired ? ' promo-expired' : ''}`}
                    hoverable={!isExpired}
                  >
                    <div className="promo-icon">
                      <TagsOutlined />
                    </div>
                    <span className="promo-off">{discountLabel(offer)}</span>
                    <div className="promo-title">{offer.title}</div>
                    {offer.description ? <p className="promo-desc">{offer.description}</p> : null}
                    <div className="promo-meta">
                      <ClockCircleOutlined />
                      {isExpired
                        ? 'Expired'
                        : offer.valid_until
                          ? `Valid until ${formatDate(offer.valid_until)}`
                          : 'Limited time'}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </section>
      ) : null}
    </div>
  );
};

export default Offers;
